/** The browser's storage for the table power features, and nothing else.
 *
 * `localStorage` is a per-viewer convenience: it throws in a private window and with site data
 * blocked, so every access is inside a `try`, a failed read means "never chosen" (the default), and a
 * failed write is dropped. The choice then lasts until the page closes, a smaller loss than a table
 * that does not render (the rule `charts.ts` and `railPrefs.ts` state for their own keys).
 *
 * **Per user, and honest about where.** The keys carry the signed-in person's id, so two people on one
 * browser do not share a default view. They are still *this browser's*: an account-level store needs
 * an endpoint that does not exist, and `shell.table-views` says so behind the views' info control.
 */

const PREFIX = "emitiq.table";

/** `emitiq.table.<what>.<user>[.<place>]` - the user is "anon" until the session has been read. */
export function tableKey(what: string, userKey: string | undefined, place?: string): string {
  return [PREFIX, what, userKey ?? "anon", ...(place ? [place] : [])].join(".");
}

export function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Blocked storage: the choice simply is not remembered.
  }
}
